import { useCallback, useMemo } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DayOfWeek } from '@/consts/date-time';
import { cn } from '@/lib/utils';
import { createUUID } from '@/lib/uuid';

export interface DaySlot {
  id: string;
  day_of_week: DayOfWeek;
  start_time: string;
  end_time: string;
}

export interface DaySchedule {
  day_of_week: DayOfWeek;
  slots: DaySlot[];
}

interface Props {
  schedule: DaySchedule;
  update: (slots: DaySlot[]) => void;
}

export function DayScheduleEditor({ schedule, update }: Props) {
  const isActive = useMemo(() => {
    return schedule.slots.length > 0;
  }, [schedule.slots]);

  const onToggle = useCallback(() => {
    update(
      isActive ? [] : [{ id: createUUID(), day_of_week: schedule.day_of_week, start_time: '09:00', end_time: '17:00' }]
    );
  }, [isActive, schedule.day_of_week, update]);

  const onAddSlot = useCallback(() => {
    update([
      ...schedule.slots,
      { id: createUUID(), day_of_week: schedule.day_of_week, start_time: '09:00', end_time: '17:00' },
    ]);
  }, [schedule.day_of_week, schedule.slots, update]);

  const onRemoveSlot = useCallback(
    (slotId: string) => {
      update(schedule.slots.filter((slot) => slot.id !== slotId));
    },
    [schedule.slots, update]
  );

  const onUpdateSlot = useCallback(
    (slotId: string, updates: Partial<DaySlot>) => {
      update(schedule.slots.map((slot) => (slot.id === slotId ? { ...slot, ...updates } : slot)));
    },
    [schedule.slots, update]
  );

  return (
    <div className='flex flex-col gap-4 border-t-[0.5px] py-3'>
      <div className='flex items-center gap-3'>
        <Switch checked={isActive} onCheckedChange={onToggle} />
        <div className='text-base font-medium capitalize'>{schedule.day_of_week.toLowerCase()}</div>
      </div>
      {isActive && (
        <div className='flex flex-col gap-4'>
          {schedule.slots.map((slot) => (
            <div key={slot.id} className='flex items-center gap-2'>
              <Input
                type='time'
                value={slot.start_time}
                onChange={(e) => onUpdateSlot(slot.id, { start_time: e.target.value })}
                className='w-32'
              />
              <span className='text-sm text-muted-foreground'>to</span>
              <Input
                type='time'
                value={slot.end_time}
                onChange={(e) => onUpdateSlot(slot.id, { end_time: e.target.value })}
                className='w-32'
              />
              <Button variant='ghost' size='icon' onClick={() => onRemoveSlot(slot.id)} className='ml-auto shrink-0'>
                <XIcon className='h-4 w-4' />
              </Button>
            </div>
          ))}
          <Button variant='link' size='sm' onClick={onAddSlot} className='h-fit w-fit px-0'>
            Add a time slot
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card className={cn('gap-4', !isActive && 'opacity-60')}>
      <CardHeader className='flex w-full flex-row items-center justify-between space-y-0'>
        <div className='flex items-center gap-3'>
          <Switch checked={isActive} onCheckedChange={onToggle} />
          <CardTitle className='capitalize'>{schedule.day_of_week.toLowerCase()}</CardTitle>
        </div>
        <Button variant='outline' onClick={onAddSlot} size='sm'>
          <PlusIcon className='mr-2 h-4 w-4' />
          Add time slot
        </Button>
      </CardHeader>
      {isActive && (
        <CardContent>
          <div className='flex flex-col gap-2'>
            {schedule.slots.map((slot) => (
              <div key={slot.id} className='flex items-center gap-2'>
                <Input
                  type='time'
                  value={slot.start_time}
                  onChange={(e) => onUpdateSlot(slot.id, { start_time: e.target.value })}
                  className='flex-1'
                />
                <span className='text-sm text-muted-foreground'>to</span>
                <Input
                  type='time'
                  value={slot.end_time}
                  onChange={(e) => onUpdateSlot(slot.id, { end_time: e.target.value })}
                  className='flex-1'
                />
                <Button variant='outline' size='icon' onClick={() => onRemoveSlot(slot.id)} className='shrink-0'>
                  <XIcon className='h-4 w-4' />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
